/**
 * Keyboard shortcuts: which keys they use, how they are spelled on screen, and
 * when they must stay out of the way.
 *
 * Page shortcuts are Option + a letter (Alt on Windows) rather than ⌘ + a
 * letter, because ⌘R, ⌘P and ⌘L already reload, print and focus the address
 * bar, and ⌘T, ⌘M and ⌘H never reach the page at all. The letters live on the
 * nav manifest entries (`shortcut`).
 *
 * On Windows the same chord is Alt + the letter, and three things make it
 * behave differently from a Mac's Option:
 *
 *   • Alt + a letter is also how a Windows browser opens its own menus, so a
 *     shortcut only reaches the page if the handler calls `preventDefault()`.
 *     That works for every letter except the ones in
 *     `BROWSER_RESERVED_LETTERS`, which the browser keeps for itself.
 *   • AltGr — the right Alt on most non-US Windows layouts — is Ctrl + Alt,
 *     and it is how those keyboards type ą, €, @ and the rest. A shortcut that
 *     read it would eat characters, so anything holding Ctrl, or reporting the
 *     AltGraph modifier, is not ours.
 *   • Alt + a letter on Windows still reports the letter in `event.key`; it is
 *     a Mac that turns Option + P into “π” and needs the physical-key fallback.
 */

/**
 * The letters a Windows browser keeps for itself, whatever the page does:
 * Alt+D focuses the address bar, Alt+E and Alt+F open the browser menu. A nav
 * entry may not claim one — `navigationManifest.test.js` fails the build on it.
 *
 * Firefox's other menu-bar letters (V, S, B, T, H) are not listed: those are
 * ordinary access keys, and a page that calls `preventDefault()` on the keydown
 * — as `useNavShortcuts` does — keeps them.
 */
export const BROWSER_RESERVED_LETTERS = ["D", "E", "F"];

/** True while someone is typing into a field, where every key is theirs. */
export function isTypingTarget(target) {
  return Boolean(
    target &&
      (target.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)),
  );
}

/** Macs spell modifier keys as symbols (⌘ ⌥); everyone else by name. */
export function isApplePlatform() {
  if (typeof navigator === "undefined") return false;
  const platform =
    navigator.userAgentData?.platform || navigator.platform || "";
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/** The keys that open search, as they are printed on this keyboard. */
export function searchKeys(apple) {
  return [apple ? "⌘" : "Ctrl", "K"];
}

/** The keys of a page shortcut, as they are printed on this keyboard. */
export function pageShortcutKeys(letter, apple) {
  return [apple ? "⌥" : "Alt", letter];
}

/**
 * A page shortcut spelled out as the steps it takes: the tooltip on its keys
 * and the description a screen reader gives the link beside them.
 */
export function pageShortcutHelp(letter, destination, apple) {
  return `Hold ${apple ? "Option" : "Alt"} and press ${letter} to go to ${destination}.`;
}

/**
 * The letter of a page shortcut this key press is, or null.
 *
 * On a Mac, Option turns P into "π", so the physical key (`code`) is the
 * fallback when `key` is not a plain letter.
 */
export function readPageShortcut(event) {
  if (!event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) {
    return null;
  }
  // AltGr on a Windows layout: the letter being typed is a character, not a
  // shortcut. Chrome reports it as Ctrl+Alt, Firefox only as the modifier.
  if (event.getModifierState?.("AltGraph")) return null;
  // Held down, or mid-IME-composition: neither is a fresh press.
  if (event.repeat || event.isComposing) return null;
  if (/^[a-z]$/i.test(event.key)) return event.key.toUpperCase();
  const match = /^Key([A-Z])$/.exec(event.code || "");
  return match ? match[1] : null;
}

/**
 * True for ⌘K / Ctrl+K, which opens the search dialog.
 *
 * Alt is excluded for the AltGr reason above: on a Windows layout Ctrl+Alt+K
 * is a character someone is typing, not a request to search.
 */
export function isSearchShortcut(event) {
  return (
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    !event.getModifierState?.("AltGraph") &&
    !event.repeat &&
    !event.isComposing &&
    typeof event.key === "string" &&
    event.key.toLowerCase() === "k"
  );
}

/** The keys that ask Guide for a line, as they are printed on this keyboard. */
export function lineShortcutKeys(apple) {
  return [apple ? "⌘" : "Ctrl", "↵"];
}

/**
 * True for ⌘↵ / Ctrl↵, the Guide floating window's "give me a line".
 *
 * A chord, not Enter-to-send: plain Enter belongs to whatever the rep is
 * typing into, so `useSubmit` still owns that. Composing is left alone, and
 * Shift or Option means some other chord.
 */
export function isLineShortcut(event) {
  return (
    event.key === "Enter" &&
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    !event.shiftKey &&
    !event.isComposing
  );
}

/**
 * True for a bare Space press outside a field: the practice composer's
 * push-to-talk key (SAL-3195). Held, it opens the mic; released, it sends
 * the turn. A chord is someone else's, and Space in a field is a space.
 */
export function isPushToTalkKey(event) {
  return (
    (event.key === " " || event.code === "Space") &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    !event.shiftKey &&
    !event.isComposing &&
    !isTypingTarget(event.target)
  );
}
